/**
 * Trainer console panel for direct Git access to the Flux Agent library repository. It shows the
 * remote, branch and latest commit, mints one-hour read or write tokens, and renders copyable
 * `git -c http.extraHeader=…` commands so the token is never stored in `.git/config`. The token is
 * held only in component state: it is never written to storage and disappears when the console
 * closes or the trainer clears it.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentGitPanel
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Check, Copy, GitBranch, KeyRound, Loader2, RefreshCw, ShieldAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  fluxAgentApi,
  shortId,
  type LibraryInfo,
  type LibraryToken,
  type LibraryTokenScope,
} from '@/lib/fluxAgentApi';
import { cn } from '@/lib/utils';
import { useCachedState } from '@/lib/uiCache';

/** Folder name used by the suggested clone command. */
const CLONE_DIRECTORY = 'flux-agent-framework';

/** Placeholder shown in commands before a token has been created. */
const TOKEN_PLACEHOLDER = '<token>';

/** One labelled shell command offered for copying. */
interface GitCommand {
  /** Short heading such as "Clone". */
  label: string;
  /** Exact shell text copied to the clipboard. */
  text: string;
}

/**
 * Build the clone, pull and (for write tokens) push commands for `remote`, authenticating each
 * call through `http.extraHeader` so the token is not saved in the clone's configuration.
 */
function gitCommands(remote: string, token: string, scope: LibraryTokenScope | null): GitCommand[] {
  const git = `git -c http.extraHeader="Authorization: Bearer ${token}"`;
  const commands: GitCommand[] = [
    { label: 'Clone', text: `${git} clone ${remote} ${CLONE_DIRECTORY}` },
    { label: 'Pull (inside the clone)', text: `${git} pull --rebase origin main` },
  ];
  if (scope !== 'read') {
    commands.push({ label: 'Push (inside the clone)', text: `${git} push origin HEAD:main` });
  }
  return commands;
}

/** Shell command block with a copy button that confirms briefly after copying.
 * @component CommandBlock
 */
function CommandBlock({
  command,
}: {
  /** Command to show and copy. */
  command: GitCommand;
}) {
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command.text);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">{command.label}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => void copy()}
          aria-label={`Copy ${command.label.toLowerCase()} command`}
        >
          {copied === 'ok' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied === 'ok' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy'}
        </Button>
      </div>
      <pre className="bg-muted whitespace-pre-wrap break-all rounded-md px-3 py-2 font-mono text-xs">
        {command.text}
      </pre>
    </div>
  );
}

/** Label/value row in the repository summary.
 * @component Fact
 */
function Fact({
  label,
  children,
}: {
  /** Field name such as "Remote". */
  label: string;
  /** Field value. */
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="break-all font-mono text-xs">{children}</dd>
    </div>
  );
}

/** Repository details, token creation and copyable git commands for trainers. */
export function FluxAgentGitPanel() {
  // Returning to this tab shows the repository details it last showed while they refresh.
  const [info, setInfo] = useCachedState<LibraryInfo | null>('flux-agent:trainer:git', null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState<LibraryTokenScope | null>(null);
  const [token, setToken] = useState<LibraryToken | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fluxAgentApi.libraryInfo();
    setLoading(false);
    if (!result.ok) return setError(result.message);
    setInfo(result.data);
  }, [setInfo]);

  useEffect(() => {
    void load();
  }, [load]);

  const mint = async (scope: LibraryTokenScope) => {
    setMinting(scope);
    setError(null);
    const result = await fluxAgentApi.createLibraryToken(scope);
    setMinting(null);
    if (!result.ok) return setError(result.message);
    setToken(result.data);
  };

  if (!info) {
    return error ? (
      <p
        role="alert"
        className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
      >
        {error}
      </p>
    ) : (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
      </p>
    );
  }

  if (!info.configured || !info.remote) {
    return (
      <div className="space-y-2 rounded-md border border-dashed p-4 text-sm">
        <p className="font-medium">Git storage is not configured.</p>
        <p className="text-muted-foreground text-xs">
          The playbook library is not connected to a Git repository on this server, so direct clone
          and push are unavailable. Ask an administrator to configure Cloudflare Artifacts.
        </p>
      </div>
    );
  }

  const remote = token?.remote || info.remote;
  const commands = gitCommands(remote, token?.token || TOKEN_PLACEHOLDER, token?.scope ?? null);
  const expires = token ? new Date(token.expiresAt) : null;

  return (
    <div className="space-y-4">
      <section aria-label="Repository" className="space-y-3 rounded-md border p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <GitBranch className="h-4 w-4" /> {info.repository}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => void load()}
            disabled={loading}
            aria-label="Refresh repository details"
          >
            <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>
        <dl className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <Fact label="Remote">{info.remote}</Fact>
          <Fact label="Branch">{info.branch}</Fact>
          <Fact label="Latest commit">
            {info.commit ? <span title={info.commit}>{shortId(info.commit)}</span> : 'none yet'}
          </Fact>
        </dl>
        <p className="text-muted-foreground text-xs">
          <code className="font-mono">system-prompt.md</code> at the root is the system prompt and
          every Markdown file under <code className="font-mono">playbooks/</code> is a playbook.
          Anything else in the repository is ignored by the agent. Every console save is a commit on{' '}
          <code className="font-mono">main</code>, and pushes to{' '}
          <code className="font-mono">main</code> take effect for new replies.
        </p>
      </section>

      {error && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
        >
          {error}
        </p>
      )}

      <section aria-label="Access token" className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {(['read', 'write'] as const).map(scope => (
            <Button
              key={scope}
              type="button"
              variant={scope === 'write' ? 'default' : 'outline'}
              onClick={() => void mint(scope)}
              disabled={minting !== null}
              className="gap-1.5"
            >
              {minting === scope ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Create {scope} token (1 hour)
            </Button>
          ))}
        </div>

        {token && expires ? (
          <div
            role="status"
            className="border-primary/30 bg-primary/5 space-y-2 rounded-md border px-3 py-2"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="flex items-start gap-1.5 text-xs">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  <span className="font-medium">
                    {token.scope === 'write' ? 'Write' : 'Read'} token created.
                  </span>{' '}
                  It is a secret: don’t share it or commit it. It expires in one hour (
                  {Number.isNaN(expires.getTime()) ? token.expiresAt : format(expires, 'h:mm a')}),
                  and it is not saved anywhere, so copy the commands now.
                </span>
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2"
                onClick={() => setToken(null)}
                aria-label="Forget token"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            Create a token to fill in the commands below. Read tokens can clone and pull; write
            tokens can also push. Tokens are secrets, expire after one hour, and are never saved.
          </p>
        )}

        <div className="space-y-3">
          {commands.map(command => (
            <CommandBlock key={command.label} command={command} />
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          From a checkout of the Fluxability repository you can also run{' '}
          <code className="font-mono">
            bun scripts/flux-agent-playbooks.ts clone {CLONE_DIRECTORY}
          </code>
          , which mints a fresh token for each command.
        </p>
      </section>
    </div>
  );
}
